import React, { useMemo, useState } from 'react';
import { Box, IconButton, useTheme } from '@material-ui/core';
import { SButton, SSelectDropdown, SText } from '@setvi/shared/components';
import NotInterestedRoundedIcon from '@material-ui/icons/NotInterestedRounded';

import { Options } from 'pages/admin/pages/users/pages/home/components/filter/components';
import { AnalyticsLabels } from 'pages/auto-rfq/components/analytics';
import {
  FilterIconV2,
  InfoIconV2,
  LabelIconV2,
  MatchIconV2,
  PointIconV2,
  SlashedFileIconV2,
  TargetIconV2
} from '@setvi/shared/components/sicons';
import { AnalyticsModules, useAnalytics } from '@setvi/shared/hooks';
import { useRfqId } from 'pages/auto-rfq/context/rfq-id';
import { AnalyticsProps, useRFQStore } from 'pages/auto-rfq/store';

export enum AnalyticsValues {
  Matched = 1,
  NonMatched = 2,
  Selected = 3,
  MfgSkuMatched = 4,
  FoundOnline = 5,
  NonMfgSkuMatched = 6,
  ExactMatch = 7,
  CloseMatch = 8,
  PossibleMatch = 9,
  UserSelection = 10,
  MarkedAsNoMatch = 11,
  RemovedItems = 12,
  Processed = 0
}

interface FilterProps {
  filters: number[];
  setFilters: React.Dispatch<React.SetStateAction<number[]>>;
  analytics: Partial<AnalyticsProps>;
}

const Filter = ({ analytics, filters, setFilters }: FilterProps) => {
  const id = useRfqId();
  const { palette } = useTheme();
  const { captureAnalytics } = useAnalytics();
  const { quoteId } = useRFQStore();

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const handleFilters = (option: number) => {
    setFilters(prev => {
      if (prev?.includes(option)) return prev.filter(item => item !== option);
      return [...prev, option];
    });

    const currFilters = [...filters, option];
    if (!currFilters?.length) return;

    const filterNames = currFilters.map(filterValue => {
      switch (filterValue) {
        case AnalyticsValues.Matched:
          return AnalyticsLabels.Matched;
        case AnalyticsValues.NonMatched:
          return AnalyticsLabels.NonMatched;
        case AnalyticsValues.ExactMatch:
          return AnalyticsLabels.ExactMatch;
        case AnalyticsValues.CloseMatch:
          return AnalyticsLabels.CloseMatch;
        case AnalyticsValues.PossibleMatch:
          return AnalyticsLabels.PossibleMatch;
        case AnalyticsValues.MfgSkuMatched:
          return AnalyticsLabels.MfgSkuMatched;
        case AnalyticsValues.NonMfgSkuMatched:
          return AnalyticsLabels.NonMfgSkuMatched;
        case AnalyticsValues.FoundOnline:
          return AnalyticsLabels.FoundOnline;
        case AnalyticsValues.MarkedAsNoMatch:
          return AnalyticsLabels.MarkedAsNoMatch;
        case AnalyticsValues.UserSelection:
          return AnalyticsLabels.UserSelection;
        case AnalyticsValues.RemovedItems:
          return AnalyticsLabels.RemovedItems;
        case AnalyticsValues.Processed:
          return AnalyticsLabels.Processed;
        default:
          return 'Unknown';
      }
    });

    captureAnalytics(
      {
        id,
        action: 'filters',
        subAction: 'status',
        query: JSON.stringify(filterNames)
      },
      AnalyticsModules.AutoRFQDetails
    );
  };

  const options = useMemo(
    () => [
      {
        icon: <TargetIconV2 fill="#5D7083" fontSize="small" />,
        value: AnalyticsValues.ExactMatch,
        label: AnalyticsLabels.ExactMatch,
        active: filters?.includes(AnalyticsValues.ExactMatch),
        total: analytics?.exactMatch,
        color: '#FAFAFB'
      },
      {
        icon: <PointIconV2 fill="#5D7083" />,
        value: AnalyticsValues.CloseMatch,
        label: AnalyticsLabels.CloseMatch,
        active: filters?.includes(AnalyticsValues.CloseMatch),
        total: analytics?.closeMatch,
        color: '#FAFAFB'
      },
      {
        icon: <MatchIconV2 fill="#5D7083" />,
        value: AnalyticsValues.PossibleMatch,
        label: AnalyticsLabels.PossibleMatch,
        active: filters?.includes(AnalyticsValues.PossibleMatch),
        total: analytics?.possibleMatch,
        color: '#FAFAFB'
      },
      {
        icon: <InfoIconV2 fill="#5D7083" />,
        value: AnalyticsValues.NonMatched,
        label: AnalyticsLabels.NonMatched,
        active: filters?.includes(AnalyticsValues.NonMatched),
        total: analytics?.notMatched,
        color: '#FAFAFB'
      },
      {
        icon: <NotInterestedRoundedIcon htmlColor="#5D7083" />,
        value: AnalyticsValues.MarkedAsNoMatch,
        label: AnalyticsLabels.MarkedAsNoMatch,
        active: filters?.includes(AnalyticsValues.MarkedAsNoMatch),
        total: analytics?.markedAsNonMatch,
        color: '#FAFAFB'
      },
      {
        icon: (
          <LabelIconV2
            fill="#5D7083"
            style={{
              transform: 'scale(0.8)'
            }}
          />
        ),
        value: AnalyticsValues.UserSelection,
        label: AnalyticsLabels.UserSelection,
        active: filters?.includes(AnalyticsValues.UserSelection),
        total: analytics?.userSelection,
        color: '#FAFAFB'
      },
      ...(quoteId
        ? [
            {
              icon: (
                <SlashedFileIconV2
                  fill="#5D7083"
                  style={{
                    transform: 'scale(0.8)'
                  }}
                />
              ),
              value: AnalyticsValues.RemovedItems,
              label: AnalyticsLabels.RemovedItems,
              active: filters?.includes(AnalyticsValues.RemovedItems),
              total: analytics?.removedFromQuotes,
              color: '#FAFAFB'
            }
          ]
        : [])
    ],
    [
      filters,
      analytics?.exactMatch,
      analytics?.closeMatch,
      analytics?.possibleMatch,
      analytics?.notMatched,
      analytics?.markedAsNonMatch,
      analytics?.userSelection,
      analytics?.removedFromQuotes,
      quoteId
    ]
  );

  return (
    <SSelectDropdown
      newDesign
      width={350}
      placement="bottom-end"
      anchorElement={anchor}
      onClose={() => setAnchor(null)}
      anchor={
        <IconButton
          onClick={e => setAnchor(e.currentTarget)}
          style={{
            padding: 8,
            borderRadius: '50%',
            border: `0.5px solid ${anchor ? palette.primary.main : '#AFBAC6'}`
          }}>
          <FilterIconV2
            htmlColor={anchor ? palette.primary.main : '#696974'}
            style={{
              transform: 'scale(0.8)'
            }}
          />
          {!!filters?.length && (
            <Box
              top={-2}
              right={0}
              width={12}
              height={12}
              borderRadius="50%"
              position="absolute"
              bgcolor={palette.primary.main}
            />
          )}
        </IconButton>
      }
      content={
        <Box>
          <Box
            padding="4px 8px 0px"
            display="flex"
            alignItems="center"
            justifyContent="space-between">
            <SText weight="bold" fontColor="#5D7083">
              Filter by status
            </SText>
            <SButton
              variant="text"
              onClick={() => setFilters([])}
              style={{
                visibility: filters?.length ? 'visible' : 'hidden'
              }}>
              Clear All
            </SButton>
          </Box>

          <Box mt={1} display="flex" alignItems="center">
            <Options
              values={filters}
              options={options}
              handleValues={handleFilters}
            />
          </Box>
        </Box>
      }
    />
  );
};

export default Filter;
